import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { StudentProtectedRoute, AdminProtectedRoute } from "@/components/feature/ProtectedRoute";

// Layout/shell components stay eager — they render immediately as part of
// the route tree's chrome. Every page component below is route-split via
// React.lazy so the initial bundle only ships the page(s) actually visited;
// AppRoutes (src/router/index.ts) wraps the router output in <Suspense>.
const NotFound = lazy(() => import("@/pages/NotFound"));
const Home = lazy(() => import("@/pages/home/page"));
const Courses = lazy(() => import("@/pages/courses/page"));
const CourseDetail = lazy(() => import("@/pages/course-detail/page"));
const About = lazy(() => import("@/pages/about/page"));
const Gallery = lazy(() => import("@/pages/gallery/page"));
const TestimonialsPage = lazy(() => import("@/pages/testimonials/page"));
const FAQ = lazy(() => import("@/pages/faq/page"));
const Contact = lazy(() => import("@/pages/contact/page"));
const Blog = lazy(() => import("@/pages/blog/page"));
const BlogDetail = lazy(() => import("@/pages/blog-detail/page"));
const Policies = lazy(() => import("@/pages/policies/page"));
const PastExpeditions = lazy(() => import("@/pages/past-expeditions/page"));
const Downloads = lazy(() => import("@/pages/downloads/page"));
const NewsEvents = lazy(() => import("@/pages/news-events/page"));

const LoginPage = lazy(() => import("@/pages/auth/login/page"));
const SignupPage = lazy(() => import("@/pages/auth/signup/page"));
const StudentLoginPage = lazy(() => import("@/pages/student/login/page"));
const AdminLoginPage = lazy(() => import("@/pages/admin/login/page"));
const AdminSetup = lazy(() => import("@/pages/admin-setup/page"));

const BookingWrapper = lazy(() => import("@/pages/booking/BookingWrapper"));
const BookingBatchPage = lazy(() => import("@/pages/booking/page"));
const BookingDetailsPage = lazy(() => import("@/pages/booking/student-details/page"));
const BookingDocumentsPage = lazy(() => import("@/pages/booking/documents/page"));
const BookingReviewPage = lazy(() => import("@/pages/booking/review/page"));
const BookingPaymentPage = lazy(() => import("@/pages/booking/payment/page"));
const BookingConfirmationPage = lazy(() => import("@/pages/booking/confirmation/page"));

const DashboardLayout = lazy(() => import("@/pages/student/DashboardLayout"));
const StudentDashboard = lazy(() => import("@/pages/student/dashboard/page"));
const StudentBookings = lazy(() => import("@/pages/student/bookings/page"));
const StudentCertificates = lazy(() => import("@/pages/student/certificates/page"));
const StudentPayments = lazy(() => import("@/pages/student/payments/page"));
const StudentProfile = lazy(() => import("@/pages/student/profile/page"));
const StudentDocuments = lazy(() => import("@/pages/student/documents/page"));
const StudentSupport = lazy(() => import("@/pages/student/support/page"));

const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/dashboard/page"));
const AdminCourses = lazy(() => import("@/pages/admin/courses/page"));
const AdminBatches = lazy(() => import("@/pages/admin/batches/page"));
const AdminBookings = lazy(() => import("@/pages/admin/bookings/page"));
const AdminPayments = lazy(() => import("@/pages/admin/payments/page"));
const AdminInvoices = lazy(() => import("@/pages/admin/invoices/page"));
const AdminExpeditions = lazy(() => import("@/pages/admin/expeditions/page"));
const AdminDownloads = lazy(() => import("@/pages/admin/downloads/page"));
const AdminNewsEvents = lazy(() => import("@/pages/admin/news-events/page"));
const AdminSupport = lazy(() => import("@/pages/admin/support/page"));
const AdminFeedback = lazy(() => import("@/pages/admin/feedback/page"));

const Story = lazy(() => import("@/pages/story/page"));
const Aim = lazy(() => import("@/pages/aim/page"));
const Campus = lazy(() => import("@/pages/campus/page"));
const Organisation = lazy(() => import("@/pages/organisation/page"));
const DirectorMessage = lazy(() => import("@/pages/about/director-message/page"));
import { Navigate } from "react-router-dom";

const routes: RouteObject[] = [
  {
    element: <Layout />,
    children: [
      { path: "/", element: <Home /> },
      { path: "/courses", element: <Courses /> },
      { path: "/courses/:slug", element: <CourseDetail /> },
      { path: "/about", element: <About /> },
      { path: "/about/story", element: <Story /> },
      { path: "/about/aim", element: <Aim /> },
      { path: "/about/campus", element: <Campus /> },
      { path: "/about/organisation", element: <Organisation /> },
      { path: "/about/director-message", element: <DirectorMessage /> },
      { path: "/gallery", element: <Gallery /> },
      { path: "/testimonials", element: <TestimonialsPage /> },
      { path: "/faq", element: <FAQ /> },
      { path: "/contact", element: <Contact /> },
      { path: "/blog", element: <Blog /> },
      { path: "/blog/:slug", element: <BlogDetail /> },
      { path: "/policies/:policy", element: <Policies /> },
      { path: "/privacy-policy", element: <Navigate to="/policies/privacy" replace /> },
      { path: "/terms-and-conditions", element: <Navigate to="/policies/terms" replace /> },
      { path: "/past-expeditions", element: <PastExpeditions /> },
      { path: "/downloads", element: <Downloads /> },
      { path: "/news-events", element: <NewsEvents /> },
    ],
  },
  { path: "/auth/login", element: <LoginPage /> },
  { path: "/auth/signup", element: <SignupPage /> },
  { path: "/student/login", element: <StudentLoginPage /> },
  { path: "/admin/login", element: <AdminLoginPage /> },
  { path: "/admin-setup", element: <AdminSetup /> },
  {
    element: <BookingWrapper />,
    children: [
      { path: "/booking/:courseSlug", element: <BookingBatchPage /> },
      { path: "/booking/:courseSlug/student-details", element: <BookingDetailsPage /> },
      { path: "/booking/:courseSlug/documents", element: <BookingDocumentsPage /> },
      { path: "/booking/:courseSlug/review", element: <BookingReviewPage /> },
      { path: "/booking/:courseSlug/payment", element: <BookingPaymentPage /> },
    ],
  },
  { path: "/booking/confirmation", element: <BookingConfirmationPage /> },
  {
    element: <StudentProtectedRoute />,
    children: [
      {
        element: <DashboardLayout />,
        children: [
          { path: "/student/dashboard", element: <StudentDashboard /> },
          { path: "/student/bookings", element: <StudentBookings /> },
          { path: "/student/certificates", element: <StudentCertificates /> },
          { path: "/student/payments", element: <StudentPayments /> },
          { path: "/student/profile", element: <StudentProfile /> },
          { path: "/student/documents", element: <StudentDocuments /> },
          { path: "/student/support", element: <StudentSupport /> },
        ],
      },
    ],
  },
  {
    element: <AdminProtectedRoute />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { path: "/admin/dashboard", element: <AdminDashboard /> },
          { path: "/admin/courses", element: <AdminCourses /> },
          { path: "/admin/batches", element: <AdminBatches /> },
          { path: "/admin/bookings", element: <AdminBookings /> },
          { path: "/admin/payments", element: <AdminPayments /> },
          { path: "/admin/invoices", element: <AdminInvoices /> },
          { path: "/admin/expeditions", element: <AdminExpeditions /> },
          { path: "/admin/downloads", element: <AdminDownloads /> },
          { path: "/admin/news-events", element: <AdminNewsEvents /> },
          { path: "/admin/support", element: <AdminSupport /> },
          { path: "/admin/feedback", element: <AdminFeedback /> },
        ],
      },
    ],
  },
  {
    element: <Layout />,
    children: [
      { path: "*", element: <NotFound /> },
    ],
  },
];

export default routes;
